import React from "react";
import {
  Box,
  Typography,
  CardContent,
  Divider,
  LinearProgress,
  Grid,
} from "@mui/material";
import { StyledCard } from "../../common/StyledCard";
import { getEntityMeta } from "../../../helpers/dashboardHelpers";
import { BankStagingStatsDto } from "../../../shared/dtos/bankTransaction/BankStagingStatsDto";
import { formatCurrency } from "../../../utils/currencyUtils";

interface BankStagingWidgetProps {
  data: BankStagingStatsDto;
}

const BankStagingWidget: React.FC<BankStagingWidgetProps> = ({ data }) => {
  const meta = getEntityMeta("BANK_STAGING");
  const Icon = meta.icon;

  return (
    <StyledCard
      sx={{ height: "100%", display: "flex", flexDirection: "column" }}
    >
      <CardContent
        sx={{ flexGrow: 1, display: "flex", flexDirection: "column" }}
      >
        <Box sx={{ display: "flex", alignItems: "center", mb: 2 }}>
          <Box
            sx={{
              p: 1,
              borderRadius: 1,
              bgcolor: `${meta.color}15`,
              color: meta.color,
              display: "flex",
              mr: 2,
            }}
          >
            <Icon />
          </Box>
          <Box>
            <Typography variant="h6" sx={{ fontWeight: "bold" }}>
              {meta.title}
            </Typography>
            <Typography variant="body2" sx={{ color: "text.secondary" }}>
              Total Transactions: {data.totalCount}
            </Typography>
          </Box>
        </Box>

        <Box sx={{ my: 2 }}>
          <Box
            sx={{ display: "flex", justifyContent: "space-between", mb: 0.5 }}
          >
            <Typography variant="body2" sx={{ color: "text.secondary" }}>
              Completion Rate
            </Typography>
            <Typography variant="body2" sx={{ fontWeight: "bold" }}>
              {data.processCompletionRate}%
            </Typography>
          </Box>
          <LinearProgress
            variant="determinate"
            value={data.processCompletionRate}
            color={data.processCompletionRate >= 80 ? "success" : "warning"}
            sx={{ height: 6, borderRadius: 3 }}
          />
        </Box>

        <Divider sx={{ my: 1.5 }} />

        <Grid container spacing={1} sx={{ textAlign: "center", my: 1 }}>
          <Grid size={{ xs: 4 }}>
            <Typography
              variant="h6"
              sx={{ fontWeight: "bold", color: "warning.main" }}
            >
              {data.pendingCount}
            </Typography>
            <Typography variant="caption" sx={{ color: "text.secondary" }}>
              Pending
            </Typography>
          </Grid>
          <Grid size={{ xs: 4 }}>
            <Typography
              variant="h6"
              sx={{ fontWeight: "bold", color: "info.main" }}
            >
              {data.matchedCount}
            </Typography>
            <Typography variant="caption" sx={{ color: "text.secondary" }}>
              Matched
            </Typography>
          </Grid>
          <Grid size={{ xs: 4 }}>
            <Typography
              variant="h6"
              sx={{ fontWeight: "bold", color: "success.main" }}
            >
              {data.postedCount}
            </Typography>
            <Typography variant="caption" sx={{ color: "text.secondary" }}>
              Posted
            </Typography>
          </Grid>
        </Grid>

        <Divider sx={{ my: 1.5 }} />

        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            mt: "auto",
          }}
        >
          <Typography variant="body2" sx={{ color: "text.secondary" }}>
            Unposted Amount
          </Typography>
          <Typography
            variant="subtitle1"
            sx={{ fontWeight: "bold", color: "error.main" }}
          >
            {formatCurrency(data.unpostedAmount)}
          </Typography>
        </Box>
      </CardContent>
    </StyledCard>
  );
};

export default BankStagingWidget;
